// TODO: Replace this client with the SDK client once available in the SDK.
import { makeAuthenticatedServiceRequest } from './servicesClient';
import type { FetchError } from 'src/types/fetch';
import type {
    DesignDataRemainingOptionsResponseType,
    DesignDataResponseType,
    DesignDataResultType,
    PanelPlacementType,
} from 'src/types/decorationSpecs';

const ENDPOINT = 'https://design-initialization-service.docext.cimpress.io';
const REQUESTER = 'scene-maker';
const DEFAULT_CULTURE = 'en-US';

// Requested in every call so the response shape stays consistent regardless of which fields Scene
// Maker currently parses.
const FIELDS = ['designViews', 'designDocument', 'printWhite', 'decorationPlacement'];

export type RetrieveDesignDataParams = {
    /** The fulfiller product SKU (CIM ID). */
    cimId: string;
    /** The fulfiller product version. */
    cimVersion?: number;
    /** The CIM's currently selected properties/variables (e.g. Size, Substrate Color). May be a
     * partial set — DIS reports back which options are still unresolved rather than erroring hard;
     * see the 400-handling below.
     *
     * Must be sourced from the picker's synchronously-updated selection (Scene Maker's own
     * `merchantVariables`), not any snapshot that's only refreshed asynchronously after full
     * resolution (Scene Maker's own `variables`) — feeding this field from the latter previously
     * caused decoration-placement data to not clear/refresh correctly on every pick. */
    variables?: Record<string, string | undefined>;
    culture?: string;
};

function buildDesignDataUrl({
    cimId,
    cimVersion,
    culture,
    variables,
}: {
    cimId: string;
    cimVersion: number;
    culture: string;
    variables: Record<string, string | undefined>;
}): string {
    const encodedSku = encodeURIComponent(cimId);
    const versionSegment = `${encodedSku}/${encodeURIComponent(String(cimVersion))}`;

    const params: string[] = [
        `culture=${encodeURIComponent(culture)}`,
        `requester=${encodeURIComponent(REQUESTER)}`,
        'fullBleed=false',
        ...FIELDS.map((field) => `fields=${encodeURIComponent(field)}`),
    ];

    // Pass every currently-selected variable/property for the CIM, not a hardcoded subset —
    // DIS Gearbox rules are keyed directly on the CIM identity, properties, and surfaces.
    Object.entries(variables).forEach(([key, value]) => {
        if (!value) {
            return;
        }
        params.push(`optionSelections${encodeURIComponent(`[${key}]`)}=${encodeURIComponent(value)}`);
    });

    return `${ENDPOINT}/v1/designData/products/${versionSegment}?${params.join('&')}`;
}

function mapDesignDataResponse(data?: DesignDataResponseType): DesignDataResultType {
    const additionalData = data?.additionalData;
    const rawPlacement = additionalData?.decorationPlacement;

    const decorationPlacement: PanelPlacementType[] | null = rawPlacement
        ? Object.entries(rawPlacement).map(([panelName, fields]) => ({
              panelName,
              distanceFromCollarToTopMm: fields.DistanceFromCollarToTopMm,
              scaledPrintWidthMm: fields.ScaledPrintWidthMm,
              scaledPrintHeightMm: fields.ScaledPrintHeightMm,
              xOffsetFromCenterMm: fields.XOffsetFromCenterMm,
          }))
        : null;

    return {
        decorationPlacement,
        printWhite: additionalData?.printWhite ?? null,
        missingSelections: null,
    };
}

/**
 * Retrieves apparel decoration-placement and print-white (white-ink underbase) data from the
 * Design Initialization Service, for the given CIM product and its currently selected properties/variables.
 *
 * DIS Gearbox rules are keyed strictly on CIM ID, CIM version, CIM properties, and CIM surfaces.
 *
 * Resolves to `{ decorationPlacement: null, printWhite: null, missingSelections: null }` for
 * products DIS has no design view for (404 — typically non-apparel, or apparel not routed for this
 * data) rather than throwing, and to a populated `missingSelections` (rather than throwing) when
 * DIS reports the given CIM/version still has unresolved options (400) — expected while a designer
 * hasn't finished picking options (e.g. Size) yet.
 */
export function retrieveDesignData({
    cimId,
    cimVersion,
    variables = {},
    culture = DEFAULT_CULTURE,
}: RetrieveDesignDataParams): Promise<DesignDataResultType> {
    if (!cimId) {
        return Promise.reject(new Error(`retrieveDesignData requires a cimId, got: cimId=${cimId}`));
    }

    if (cimVersion === undefined) {
        return Promise.reject(new Error(`retrieveDesignData requires a cimVersion, got: cimId=${cimId}`));
    }
    const url = buildDesignDataUrl({ cimId, cimVersion, culture, variables });

    return makeAuthenticatedServiceRequest<DesignDataResponseType>({ url }).then(
        (response) => mapDesignDataResponse(response?.data),
        (error: FetchError) => {
            const status = error?.response?.status;

            if (status === 404) {
                return { decorationPlacement: null, printWhite: null, missingSelections: null };
            }

            if (status === 400) {
                const details = (error.response?.data as DesignDataRemainingOptionsResponseType)?.details;
                const missingSelections = [
                    ...(details?.remainingOptions ?? []),
                    ...(details?.remainingVariables ?? []),
                ];
                if (missingSelections.length > 0) {
                    return { decorationPlacement: null, printWhite: null, missingSelections };
                }
            }

            throw error;
        },
    );
}
